/* ============================================================================
   Lista de barras. Dos modos:

   · `magnitude` — comparar cuánto. Una sola categoría nominal ⇒ UN color
     (slot 1) para todas las barras; nunca una rampa por valor, porque eso
     gastaría el canal de identidad en re-codificar el largo de la barra.
   · `diverging`  — polaridad respecto a cero. Naranja = encareció, azul = abarató,
     con punto neutro gris. El signo y la flecha son el canal redundante, así que
     el color nunca carga solo el sentido.

   Barras de 10px con extremo de dato redondeado 4px y escuadrado en la base.
   Cada valor es visible como texto: la fila no depende del hover.
   ========================================================================== */

import { useId, useMemo } from 'react'
import { barPath } from './primitives'
import { Delta } from '../components/Delta'
import './barList.css'

export interface BarRow {
  id: string
  label: string
  /** Segunda línea: unidad, presentación o categoría. */
  sublabel?: string
  /** Valor absoluto que se imprime en la columna de valor. */
  value: number
  /** Magnitud que dibuja la barra. Negativa en modo diverging. `null` = sin dato. */
  magnitude: number | null
}

export interface BarListProps {
  rows: BarRow[]
  title: string
  subtitle?: string
  /**
   * Encabezado de la primera columna. La lista se usa para productos y para
   * lugares, así que no puede decir «Producto» siempre: un ranking de estados
   * bajo un encabezado «Producto» es un error de lectura servido en bandeja.
   */
  nameHeading?: string
  /** Resumen para lector de pantalla. Debe describir lo que la tabla contiene. */
  caption?: string
  /** Encabezado de la columna de valor. */
  valueHeading: string
  /** Encabezado de la columna de barra. */
  magnitudeHeading: string
  formatValue: (value: number) => string
  /**
   * Formato de la cifra que acompaña a la barra, cuando su unidad NO es la misma
   * que la de la columna de valor. Por omisión reusa `formatValue`, que es lo
   * correcto mientras las dos columnas midan lo mismo; cuando una lleva pesos y
   * la otra por ciento, compartir formateador imprimía el precio como porcentaje.
   */
  formatMagnitude?: (value: number) => string
  mode?: 'magnitude' | 'diverging'
  /** En modo diverging: subir es favorable. */
  invert?: boolean
  /** Unidad de la magnitud, para el modo diverging es porcentaje. */
  magnitudeKind?: 'percent' | 'currency'
  /**
   * Color de la barra en modo `magnitude`.
   *
   * `brand` es el azul del sistema y sirve cuando la barra mide un monto —un
   * precio— y no una dirección. `alza` pinta el naranja de «encareció» y existe
   * para cuando la lista muestra variaciones que resultaron ser todas positivas:
   * ahí el azul es el MISMO hex que `--div-down`, o sea el color con el que esta
   * app dice «bajó», y una tabla de puras alzas pintada de «bajó» enseña al
   * lector lo contrario de lo que muestra la pestaña de al lado.
   */
  magnitudeTone?: 'brand' | 'alza'
  /**
   * Unidad de la columna de valor.
   *
   * Sirve para decidir si en pantalla angosta hay que MOSTRAR el rótulo de cada
   * celda o basta con dejarlo para el lector de pantalla. Bajo 640 px el `thead`
   * desaparece, y con dos columnas de distinta unidad —pesos y por ciento— se
   * distinguen solas. Cuando las dos son porcentajes no: quedan dos cifras como
   * «+62.8 %» y «+7.4 %» sin nada que diga cuál es el acumulado y cuál el año.
   */
  valueKind?: 'percent' | 'currency'
  footnote?: string
  emptyMessage?: string
  /** Si se pasa, cada renglón se vuelve accionable (botón, no `div` con onClick). */
  onRowClick?: (id: string) => void
}

const BAR_H = 10
const BAR_RADIUS = 4
const COL_W = 132

export function BarList({
  rows,
  title,
  subtitle,
  nameHeading,
  caption,
  valueHeading,
  magnitudeHeading,
  formatValue,
  formatMagnitude,
  mode = 'magnitude',
  invert = false,
  magnitudeKind = 'percent',
  magnitudeTone = 'brand',
  valueKind,
  footnote,
  emptyMessage = 'Sin datos para este corte.',
  onRowClick,
}: BarListProps) {
  const uid = useId().replace(/[^a-zA-Z0-9-]/g, '')

  /* Con las dos columnas en la misma unidad, el rótulo de celda deja de ser
     redundante en pantalla angosta y pasa a ser lo único que las distingue. */
  const cellLabelClass =
    valueKind !== undefined && valueKind === magnitudeKind
      ? 'barlist__cell-label'
      : 'sr-only-mobile'

  const scaleMax = useMemo(() => {
    const values = rows
      .map((r) => (r.magnitude === null ? Number.NaN : Math.abs(r.magnitude)))
      .filter((v) => Number.isFinite(v))
    const max = values.length > 0 ? Math.max(...values) : 1
    return max === 0 ? 1 : max
  }, [rows])

  if (rows.length === 0) {
    return (
      <figure className="barlist">
        <figcaption className="barlist__head">
          <h3 className="t-title3" id={`${uid}-title`}>
            {title}
          </h3>
          {subtitle ? <p className="t-footnote ink-3">{subtitle}</p> : null}
        </figcaption>
        <p className="barlist__empty t-footnote ink-3">{emptyMessage}</p>
      </figure>
    )
  }

  return (
    <figure className="barlist" aria-labelledby={`${uid}-title`}>
      <figcaption className="barlist__head">
        <div>
          <h3 className="t-title3" id={`${uid}-title`}>
            {title}
          </h3>
          {subtitle ? <p className="t-footnote ink-3 barlist__subtitle">{subtitle}</p> : null}
        </div>
      </figcaption>

      {/* Roles explícitos: en móvil el CSS apila las filas con `display: grid`,
          y eso borra los roles de tabla que el navegador deriva del `display`.
          Declararlos a mano los conserva en los dos anchos. */}
      <table className="barlist__table" role="table">
        {/* El figcaption ya da título y subtítulo visibles; el caption sólo
            aporta lo que la tabla necesita y aquél no dice. */}
        <caption className="sr-only">
          {caption ?? 'Precio por unidad y variación, por renglón.'}
        </caption>
        <thead>
          <tr>
            <th scope="col" className="barlist__th-name t-micro ink-3">
              {nameHeading ?? 'Producto'}
            </th>
            <th scope="col" className="barlist__th-value t-micro ink-3">
              {valueHeading}
            </th>
            <th scope="col" className="barlist__th-bar t-micro ink-3">
              {magnitudeHeading}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            /* Sin dato no es cero: no se dibuja barra y se dice explícitamente.
               Antes un `?? 0` convertía «no hay dato de hace un año» en
               «0.0 % sin cambio», que es afirmar algo falso. */
            const missing = row.magnitude === null
            const magnitude = row.magnitude ?? 0
            const ratio = missing ? 0 : Math.min(1, Math.abs(magnitude) / scaleMax)
            const isNeutral = Math.abs(magnitude) < 0.05
            const positive = magnitude > 0
            let d = ''
            let fill = magnitudeTone === 'alza' ? 'var(--div-up)' : 'var(--fill-magnitude)'

            if (mode === 'diverging') {
              const half = COL_W / 2
              const w = ratio * (half - 2)
              if (missing) {
                d = ''
                fill = 'transparent'
              } else if (isNeutral) {
                d = barPath(half - 1.5, 0, 3, BAR_H, 1.5, 'right')
                fill = 'var(--div-mid)'
              } else if (positive) {
                d = barPath(half, 0, w, BAR_H, BAR_RADIUS, 'right')
                fill = invert ? 'var(--div-down)' : 'var(--div-up)'
              } else {
                d = barPath(half - w, 0, w, BAR_H, BAR_RADIUS, 'left')
                fill = invert ? 'var(--div-up)' : 'var(--div-down)'
              }
            } else {
              d = missing ? '' : barPath(0, 0, Math.max(2, ratio * COL_W), BAR_H, BAR_RADIUS, 'right')
            }

            return (
              <tr key={row.id} role="row" className={`barlist__row${onRowClick ? ' barlist__row--clickable' : ''}`}>
                {/* `aria-label` en el `th` fija el nombre del encabezado de fila
                    —que se repite al leer cada celda— al del producto, sin la
                    afordancia. Dentro, el botón se nombra con el producto Y su
                    unidad: la tabla mezcla kg y litros, y sin eso el precio
                    llegaba sin unidad a quien no ve la sublínea. */}
                <th
                  scope="row"
                  role="rowheader"
                  className="barlist__name"
                  aria-label={row.sublabel ? `${row.label}, ${row.sublabel}` : row.label}
                >
                  {onRowClick ? (
                    <button
                      type="button"
                      className="barlist__name-button"
                      aria-label={
                        row.sublabel
                          ? `${row.label}, ${row.sublabel}. Ver la serie mensual completa`
                          : `${row.label}. Ver la serie mensual completa`
                      }
                      onClick={() => onRowClick(row.id)}
                    >
                      <span className="barlist__name-main t-callout" aria-hidden="true">
                        {row.label}
                      </span>
                      {row.sublabel ? (
                        <span className="barlist__name-sub t-caption ink-3" aria-hidden="true">
                          {row.sublabel}
                        </span>
                      ) : null}
                    </button>
                  ) : (
                    <>
                      <span className="barlist__name-main t-callout">{row.label}</span>
                      {row.sublabel ? (
                        <span className="barlist__name-sub t-caption ink-3">{row.sublabel}</span>
                      ) : null}
                    </>
                  )}
                </th>
                <td role="cell" className="barlist__value tabular t-callout">
                  {/* El encabezado de columna se oculta en móvil, así que cada
                      celda dice qué es. En escritorio el thead sí está, y esta
                      etiqueta duplicaría: `sr-only-mobile` la espeja exactamente. */}
                  <span className={cellLabelClass}>{valueHeading}: </span>
                  {formatValue(row.value)}
                </td>
                <td role="cell" className="barlist__bar">
                  <span className={cellLabelClass}>{magnitudeHeading}: </span>
                  <div className="barlist__bar-inner">
                    <svg
                      width={COL_W}
                      height={BAR_H}
                      viewBox={`0 0 ${COL_W} ${BAR_H}`}
                      aria-hidden="true"
                      focusable="false"
                    >
                      {mode === 'diverging' ? (
                        <line
                          x1={COL_W / 2 + 0.5}
                          x2={COL_W / 2 + 0.5}
                          y1={-2}
                          y2={BAR_H + 2}
                          className="barlist__zero"
                        />
                      ) : null}
                      <path d={d} fill={fill} />
                    </svg>
                    {missing ? (
                      <span className="barlist__magnitude t-caption ink-3">sin dato</span>
                    ) : /* Una variación se imprime SIEMPRE con `Delta`, dibuje la
                          barra polaridad o no. `Delta` carga el signo, la flecha, el
                          tono y un «aumento/disminución» para lector de pantalla; el
                          `toFixed` crudo que había aquí los perdía todos y sacaba un
                          «75.8 %» pelado bajo el encabezado «Desde julio 2018», que es
                          justo la cifra que se lee como nivel. La rama de moneda sí
                          es un monto, no un cambio, y se queda como estaba. */
                      mode === 'diverging' || magnitudeKind === 'percent' ? (
                      <Delta value={magnitude} kind={magnitudeKind} invert={invert} />
                    ) : (
                      <span className="barlist__magnitude tabular t-footnote ink-2">
                        {(formatMagnitude ?? formatValue)(magnitude)}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {footnote ? <p className="t-caption ink-3 barlist__footnote">{footnote}</p> : null}
    </figure>
  )
}
